import {nextTick} from 'vue';

import {callAPI} from '@/store/utils'; // eslint-ignore-line
import {sendErrorToBackend, userDetails} from '@/utils/errorHandler.js';
import {shouldWarnSubMonthly} from '@/lib/subMonthlyForecastNotice';

const FYGR_COMPANY = 'FYGR_COMPANY';
const FYGR_ACCESS_TOKEN = 'FYGR_ACCESS_TOKEN';
const FYGR_PRETENDED_USER = 'FYGR_PRETENDED_USER';
const COOKIE_NAME = '_aw_master_id';
let controller = new AbortController();
let {signal} = controller;
const urlBase = `${import.meta.env.VITE_APP_API}/v2`;

function getCookieDomain() {
  const parts = window.location.hostname.split('.');
  return parts.length >= 2
    ? `.${parts.slice(-2).join('.')}`
    : window.location.hostname;
}

const updateSignal = () => {
  controller.abort();
  controller = new AbortController();
  /* eslint-disable */
  signal = controller.signal;
  /* eslint-enable */
  return signal;
};

export default {
  namespaced: true,
  state: {
    gotBanks: false,
    banks: null,
    rules: [],
    savedRules: [],
    currencyList: [],
    dashboardSelectedAccounts: null,
    MAList: null,
    // Set true right before a balance-only refetch (e.g. the live
    // payment_account_credited event) so the dashboard's `banks` watcher can
    // update the reactive balances without redrawing the chart / reloading the
    // table (getDashboardChartData, getMAComments, …).
    balanceOnlyRefresh: false,
    subMonthlyNotice: false,
    accountsByCompany: {},
  },
  getters: {
    bankByAccountId(state) {
      const banks = {};
      Object.values(state.accountsByCompany || {}).forEach((accounts) =>
        accounts.forEach((account) => {
          banks[account.id] = {
            id: account.bank_id,
            name: account.bank_custom_name || account.bank_name || '',
          };
        }),
      );
      return banks;
    },
    accountNameByAccountId(state) {
      const names = {};
      Object.values(state.accountsByCompany || {}).forEach((accounts) =>
        accounts.forEach((account) => {
          names[account.id] = account.custom_name || account.name || '';
        }),
      );
      return names;
    },
    accountsDDOptions(state) {
      return state.banks
        ? state.banks.map((bank) => {
            return {
              name: bank.custom_name || bank.name,
              id: bank.id,
              showLogo: true,
              param: {selectAll: true},
              list: bank.accounts
                ? bank.accounts.map((account, index) => {
                    return {
                      company_id: 'bce347e0-bf1a-11ed-9ce4-5d26b217f146',
                      name:
                        account.custom_name ||
                        account.name ||
                        (bank.custom_name
                          ? `${bank.custom_name} (${index + 1})`
                          : `${bank.name} (${index + 1})`),
                      parentId: bank.id,
                      aggregator: bank.aggregator,
                      type: bank.type,
                      parentName: bank.custom_name || bank.name,
                      level: 1,
                      deepest: true,
                      id: account.id,
                      iban: account.iban,
                      is_alterable: true,
                      forecast_enabled: true,
                      balance: account.balance,
                      currency: account.currency,
                      param: {selectAll: false},
                    };
                  })
                : [],
            };
          })
        : [];
    },
    bankNumber(state) {
      return state.banks ? state.banks.length : 0;
    },
    currencyListSorted(state) {
      return state.currencyList.map((itm) => {
        const res = {
          name: itm.iso,
          id: itm.id.toString(),
        };
        return res;
      });
    },
    currencyListDropDown(state) {
      return state.currencyList.map((itm) => [itm.id, itm.iso]);
    },
    pinnedBanks(state) {
      return state.banks?.map((b) => ({
        ...b,
        accounts: b.accounts.filter((a) => a.pinned),
      }));
    },
    pinnedAccountsList(state) {
      return state.banks
        ?.reduce((acc, el) => [...acc, ...el.accounts], [])
        .filter((account) => account.pinned)
        .map((a) => a.id);
    },
    allAccountsList(state) {
      return state.banks?.reduce(
        (acc, el) => [
          ...acc,
          ...el.accounts.map((account) => ({
            ...account,
            type: el.type,
            bank_name: el.name,
            bank_custom_name: el.custom_name,
          })),
        ],
        [],
      );
    },
    ebicsAccountsList(state) {
      if (!state.banks) return [];
      return state.banks
        .filter((bank) => bank.type === 'ebics')
        .flatMap((bank) =>
          bank.accounts.map((account) => ({
            ...account,
            type: bank.type,
            aggregator: bank.aggregator,
            bank_name: bank.name,
            bank_custom_name: bank.custom_name,
            bankId: bank.id,
          })),
        );
    },
    accountsListWithIbans(state) {
      return state.banks
        ? state.banks
            ?.reduce(
              (acc, bank) => [
                ...acc,
                ...bank.accounts.map((account) => ({
                  id: account.id,
                  balance: account.balance,
                  currency: account.currency,
                  iban: account.iban,
                  pinned: account.pinned,
                  bic: account.bic || null,
                  type: bank.type,
                  accountName: account.custom_name || account.name,
                  bankName: bank.custom_name || bank.name,
                })),
              ],
              [],
            )
            .filter((account) => !!account.iban)
        : [];
    },
  },
  mutations: {
    setDashboardSelectedAccounts(state, accs) {
      state.dashboardSelectedAccounts = accs;
    },
    setBanks(state, banks) {
      state.banks = banks;
      state.gotBanks = true;
    },
    setBalanceOnlyRefresh(state, val) {
      state.balanceOnlyRefresh = val;
    },
    setSubMonthlyNotice(state, val) {
      state.subMonthlyNotice = val;
    },
    setCompanyAccounts(state, {companyId, banks}) {
      state.accountsByCompany = {
        ...state.accountsByCompany,
        [companyId]: (banks || []).flatMap((bank) =>
          (bank.accounts || []).map((account) => ({
            ...account,
            bank_id: bank.id,
            bank_name: bank.name,
            bank_custom_name: bank.custom_name,
          })),
        ),
      };
    },
    unauthBanks(state) {
      state.gotBanks = false;
      state.banks = null;
      state.accountsByCompany = {};
      state.rules = [];
      state.savedRules = [];
    },
    editRule(state, rule) {
      const index = state.rules.findIndex((r) => r.id === rule.id);
      state.rules[index] = rule;
    },
    setCurrencyList(state, list) {
      state.currencyList = list.sort((a, b) => {
        if (a.iso > b.iso) {
          return 1;
        }
        if (a.iso < b.iso) {
          return -1;
        }
        return 0;
      });
    },
    setMAList(state, list) {
      state.MAList = list;
    },
    setRules(state, rules) {
      state.rules = rules;
    },
    setSavedRules(state, rules) {
      state.savedRules = rules;
    },
    addRule(state, rule) {
      state.rules.push(rule);
    },
    deleteRule(state, id) {
      const index = state.rules.findIndex((r) => r.id === id);
      state.rules.splice(index, 1);
    },
    addBank(state, params) {
      if (!state.banks) {
        state.banks = [
          {
            id: params.bankId,
            last_update: params.lastUpdate,
            name: params.name,
            custom_name: null,
            accounts: [],
            state: {
              status: 'working',
              provider_code: 'active',
              action_required_fygr: false,
              fygr_description: 'Connexion opérationnelle.',
              fygr_short_message: 'Connexion active',
            },
          },
        ];
      } else {
        state.banks.push({
          id: params.bankId,
          last_update: params.lastUpdate,
          name: params.name,
          custom_name: null,
          accounts: [],
          state: {
            status: 'working',
            provider_code: 'active',
            action_required_fygr: false,
            fygr_description: 'Connexion opérationnelle.',
            fygr_short_message: 'Connexion active',
          },
        });
      }
    },
    addAccount(state, params) {
      const banks = [...state.banks];
      const idx = banks.findIndex((b) => b.id === params.connectionId);
      banks[idx].accounts.push({
        id: params.accountId,
        balance: 0,
        currency: 'EUR',
        name: params.name,
        custom_name: null,
      });
      state.banks = [...banks];
    },
    // The four mutations below optimistically patch `state.banks`, which only
    // ever holds the *current* company's connections. /console configures the
    // banks of another entity, so the connection is legitimately absent there:
    // bail out instead of indexing `banks[-1]`.
    pinAccounts(state, params) {
      const banks = [...state.banks];
      const bidx = banks.findIndex((b) => b.id === params.bank_id);
      if (bidx < 0) {
        return;
      }
      params.account_ids.map((id) => {
        const aidx = banks[bidx].accounts.findIndex((a) => a.id === id);
        if (aidx < 0) {
          return null;
        }
        banks[bidx].accounts.splice(aidx, 1, {
          ...banks[bidx].accounts[aidx],
          pinned: params.pinned,
        });
        return null;
      });
      state.banks = [...banks];
    },
    updateAccountName(state, params) {
      const banks = [...state.banks];
      const bidx = banks.findIndex((b) => b.id === params.connection_id);
      if (bidx < 0) {
        return;
      }
      const aidx = banks[bidx].accounts.findIndex(
        (a) => a.id === params.account_id,
      );
      if (aidx < 0) {
        return;
      }
      banks[bidx].accounts[aidx].custom_name = params.name;
      state.banks = [...banks];
    },
    deleteAccount(state, params) {
      const banks = [...state.banks];
      const bidx = banks.findIndex((b) => b.id === params.bankId);
      if (bidx < 0) {
        return;
      }
      const aidx = banks[bidx].accounts.findIndex(
        (a) => a.id === params.accountId,
      );
      if (aidx < 0) {
        return;
      }
      banks[bidx].accounts.splice(aidx, 1);
      state.banks = [...banks];
    },
    updateConnectionName(state, params) {
      const banks = [...state.banks];
      const bidx = banks.findIndex((b) => b.id === params.connection_id);
      if (bidx < 0) {
        return;
      }
      banks[bidx].custom_name = params.name;
      state.banks = [...banks];
    },
  },
  actions: {
    async actionGetAccountsPendingDeletion() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${companyId}/accounts/pending-deletion`,
      );
      return res?.error ? [] : res?.result || [];
    },
    async actionReviewAccountDeletion(_, {accountExId, confirmed}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      return callAPI(
        'POST',
        `company/${companyId}/account/${accountExId}/deletion-review`,
        {confirmed},
      );
    },
    // `params.companyId` lets /console act on a listed entity; everywhere else
    // it is omitted and the current company applies.
    async actionDeleteAccount({commit}, params) {
      const companyId = params.companyId || localStorage.getItem(FYGR_COMPANY);
      commit('deleteAccount', params);
      const res = await callAPI('POST', 'deleteAccount', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionGetAccountsAwaitingApproval(_, params = {}) {
      const companyId = params.companyId || localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${companyId}/accounts/awaiting-approval`,
      );
      return res?.error ? [] : res?.result || [];
    },
    async actionReviewAccount(_, {accountExId, approved, companyId}) {
      const company = companyId || localStorage.getItem(FYGR_COMPANY);
      return callAPI('POST', `company/${company}/account/${accountExId}/approval`, {
        approved,
      });
    },
    async actionRenameAccount({commit}, params) {
      const companyId = params.companyId || localStorage.getItem(FYGR_COMPANY);
      commit('updateAccountName', params);
      const res = await callAPI('POST', 'updateAccountName', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionRenameConnection({commit}, params) {
      const companyId = params.companyId || localStorage.getItem(FYGR_COMPANY);
      commit('updateConnectionName', params);
      const res = await callAPI('POST', 'updateConnectionName', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionPinAccounts({commit}, params) {
      const companyId = params.companyId || localStorage.getItem(FYGR_COMPANY);
      commit('pinAccounts', params);
      const res = await callAPI('POST', 'pinAccounts', {...params, companyId});
      return res;
    },
    async actionAddManualConnection({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      commit('addBank', params);
      const res = await callAPI('POST', 'addManualConnection', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionUnauthBanks({commit}) {
      commit('unauthBanks');
    },
    async actionAddManualAccount({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      commit('addAccount', params);
      const res = await callAPI('POST', 'addManualAccount', {
        ...params,
        companyId,
      });
      return res.result;
    },
    async actionGetRules({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'listSubCategoryRules', {
        ...params,
        companyId,
      });
      if (res.result) {
        commit('setRules', res.result.rules_list);
        commit('setSavedRules', res.result.rules_list);
      }
      return res.result;
    },
    async actionDownloadCategorizationRules() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${companyId}/subCategoryRules/download`,
        {},
        true,
        20,
        0,
        true,
      );
      return res;
    },
    async actionGetAffectedTransactions(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getAffectedTransactions', {
        companyId,
        ...params,
      });
      return res;
    },
    async actionGetAffectedInvoices(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getInvoicesAffectedByRule', {
        companyId,
        ...params,
      });
      return res;
    },
    // `timeout` / `recall` sont des réglages d'appel, pas des filtres : ils ne
    // partent pas dans le corps. Un appelant pressé (bannière d'accueil) peut
    // ainsi se donner quelques secondes et renoncer, sans changer le
    // comportement par défaut des autres.
    async actionGetSuggestions(_, {timeout, recall, ...params} = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'getSuggestionsList',
        {companyId, ...params},
        false,
        timeout ?? 20,
        recall ?? 0,
      );
      if (res?.error) return res;
      // La réponse a changé de forme : la liste arrivait nue (`result: [...]`),
      // elle arrive désormais sous `result.suggestions`, accompagnée de
      // `result.eligibility`. On normalise ici — un seul contrat pour les
      // appelants, et l'ancienne forme reste acceptée le temps du déploiement.
      const result = res?.result;
      return {
        ...res,
        result: {
          suggestions: Array.isArray(result) ? result : (result?.suggestions ?? []),
          eligibility: Array.isArray(result) ? null : (result?.eligibility ?? null),
        },
      };
    },
    async actionSwitchSuggestions(_, {suggestionsEnabled, withList}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'switchSuggestions', {
        companyId,
        suggestionsEnabled,
        withList,
      });
      return res;
    },
    async actionDeleteSuggestion(_, suggestionId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'deleteSuggestion', {
        companyId,
        suggestionId,
      });
      return res;
    },
    async actionSetRules({commit, state}, {rules, trackId}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      commit('setRules', rules);
      const res = await callAPI(
        'POST',
        'setSubCategoryRules',
        {rules, companyId, trackId, originalRules: state.savedRules},
        false,
        60,
        0,
      );
      return res;
    },
    async actionAddSingleRule({commit}, data) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      commit('setRules', data.rules);
      const res = await callAPI(
        'PUT',
        'createNewSubCategoryRule',
        {
          rule: data.rule,
          companyId,
        },
        false,
        40,
      );
      return res;
    },
    async actionCheckRuleConflicts(_commit, rule) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'checkRulesConflicts', {
        rule,
        companyId,
      });
      return res.result;
    },
    async actionReindexTransactions() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'reindexTransactions', {companyId});
    },
    async actionGetBankAccount(_, code) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getBankAccount', {code, companyId});
      return res.result;
    },
    async actionGetBankAccountFromProvider(_, params) {
      const res = await callAPI('POST', 'getBankAccounts', params);
      if (res && !res.error) {
        return res.result;
      }
    },
    async actionLoadTransactions({dispatch}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      dispatch('app/actionSetReloading', true, {root: true});
      const res = await callAPI(
        'POST',
        'loadTransactions',
        {companyId},
        false,
        0,
      );
      await new Promise((resolve) => {
        setTimeout(() => resolve(), 30000);
      });
      await dispatch('actionGetBanksAndAccountsList');
      dispatch('app/actionSetReloading', false, {root: true});
      if (res && !res.error) {
        return res.result;
      }
    },
    async actionGetBanksAndAccountsList({commit}, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getBanksAndAccountsList', {
        ...request,
        companyId,
      });
      commit('setBanks', res?.result?.banks || []);
    },
    async actionRefreshBanksBalancesLive({commit, dispatch}) {
      // Flag set before the commit so the dashboard's `banks` and `pinnedBanks`
      // watchers both see it on the same reactive tick and skip all side
      // handling (chart redraw / getDashboardChartData, MA comments reload, …)
      // — the new balances are read reactively anyway. Cleared after the
      // watcher flush (nextTick) so both watchers read a stable value and the
      // flag never stays stuck if a refetch returns identical data.
      commit('setBalanceOnlyRefresh', true);
      await dispatch('actionGetBanksAndAccountsList');
      await nextTick();
      commit('setBalanceOnlyRefresh', false);
    },
    async actionGetBanksForMC({commit}, request) {
      // Guide « entities-consolidation-onboarding » : l'entreprise de démo
      // n'existe pas côté serveur → on renvoie une banque + un compte FICTIFS
      // (client-only, aucun appel API) pour qu'Okimia soit cochable dans la
      // consolidation. Sans compte, aucune case à cocher ne s'affiche.
      try {
        if (
          request.companyId &&
          request.companyId === localStorage.getItem('fygr-tour-company')
        ) {
          const tourBanks = [
            {
              id: `tour-bank-${request.companyId}`,
              name: 'Banque de démonstration',
              custom_name: null,
              accounts: [
                {
                  id: `tour-account-${request.companyId}`,
                  name: 'Compte de démonstration',
                  custom_name: null,
                  balance: 0,
                  currency: request.currency_iso || 'EUR',
                  exchange_rate: 1,
                },
              ],
            },
          ];
          commit('setCompanyAccounts', {
            companyId: request.companyId,
            banks: tourBanks,
          });
          return tourBanks;
        }
      } catch {
        /* ignore */
      }
      const res = await callAPI('POST', 'getBanksAndAccountsList', request);
      commit('setCompanyAccounts', {
        companyId: request.companyId,
        banks: res.result.banks,
      });
      return res.result.banks;
    },
    async actionDeleteConnection({dispatch}, data) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'deleteConnection',
        typeof data === 'object'
          ? {...data, companyId}
          : {bank_id: data, companyId},
      );
      await dispatch('actionGetBanksAndAccountsList');
      return res.result;
    },
    async cleanCategoryAdjustments(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'cleanCategoryAdjustments', {
        ...params,
        companyId,
      });
      return res;
    },
    // `payload` is either a bank id (current company) or {bankId, companyId} —
    // /console lists the banks of another entity, so the current company held in
    // localStorage would be the wrong one there.
    async actionGetModifyUrl({dispatch}, payload) {
      const {bankId, companyId} =
        payload && typeof payload === 'object'
          ? payload
          : {bankId: payload, companyId: localStorage.getItem(FYGR_COMPANY)};
      // A 204 (no config url for that connection) makes callAPI reject with the
      // raw Response, so this must be guarded or the click throws and the button
      // looks dead.
      let res;
      try {
        res = await callAPI('POST', 'getModifyUrl', {
          bank_id: bankId,
          companyId: companyId || localStorage.getItem(FYGR_COMPANY),
        });
      } catch (err) {
        res = null;
        console.warn('getModifyUrl failed', err);
      }
      // Without the url there is nowhere to redirect. Tell the user instead of
      // failing silently, which made the button look dead.
      if (!res?.result?.url) {
        dispatch(
          'app/actionAddNotification',
          {
            msg: "La configuration de cette connexion bancaire n'est pas disponible pour le moment.",
            color: 'red',
            disappearing: true,
          },
          {root: true},
        );
        console.warn('getModifyUrl returned no url', res);
        return res?.result ?? null;
      }
      window.location = res.result.url;
      return res.result;
    },
    async actionUnsyncBankConnection() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'unsyncBankConnection', {
        companyId,
        aggregatorName: 'bridge',
      });
      return res;
    },
    async actionGetTransaction(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getTransaction', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionImportTransactionSplitFile(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'addManualChildrenTransactions', {
        ...params,
        companyId,
      });
      return res;
    },
    // FIXME:TT-3331
    async actionGetTransactions(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'getTransactionList',
        {
          ...request,
          companyId,
        },
        false,
        0,
      );
      return res.result;
    },
    async actionExportDashboardXLSX(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'exportDashboardXLSX',
        {...request, companyId},
        false,
        60,
        0,
      );
      return res;
    },
    async exportDashboardConsolidationTableXLSX(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'exportDashboardConsolidationTableXLSX',
        {...request, companyId},
        false,
        60,
        0,
      );
      return res;
    },
    async actionSetBankingTransactionsCategory(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'setBankingTransactionsCategory',
        {...request, companyId},
        false,
        30,
        3,
      );
      return res;
    },
    async actionUpdateBankingTransactions(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'PUT',
        'updateTransaction',
        {...request, companyId},
        false,
        30,
        3,
      );
      return res;
    },
    async actionDownloadTransactions(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'downloadTransactions', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionDownloadInvoices(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'downloadInvoices', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionSetTransactionStatus(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'setTransactionStatus', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionGetDashboardChartData(_, params) {
      const companyId = params.companyId || localStorage.getItem(FYGR_COMPANY);
      const newSignal = params.companyId ? null : updateSignal();
      const res = await callAPI(
        'POST',
        'getDashboardChartData',
        {...params, companyId},
        false,
        120,
        0,
        false,
        newSignal,
      );
      return res.result;
    },
    async actionUploadAttachments(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'uploadAttachments', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionGetPresignedUrlForAttachment(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getPresignedUrlForAttachment', {
        ...params,
        companyId,
      });
      return res.result;
    },
    async actionSaveTransactionComment(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'setTransactionComment', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionSetTransactionValidated(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'setTransactionValidated', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionSetTransactionReceiptStatus(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'setTransactionReceiptStatus', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionDeleteAttachment(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'deleteAttachment', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionGetDashboardTableData(_, params) {
      const companyId = params.companyId || localStorage.getItem(FYGR_COMPANY);
      const newSignal = params.companyId ? null : updateSignal();
      const res = await callAPI(
        'POST',
        'getDashboardChartData',
        {...params, companyId},
        false,
        120,
        0,
        false,
        newSignal,
      );
      return res.result;
    },
    async actionGetOldestTransactionDate() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getOldestTransactionDate', {
        companyId,
      });
      return res;
    },
    async actionGetDashboardTransactions(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getDashboardTransactions', {
        ...params,
        companyId,
      });
      return res.result;
    },
    async actionAddManualAdjustment({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'addManualAdjustment', {
        ...params,
        companyId,
      });
      if (!res.error && shouldWarnSubMonthly(params.period)) {
        commit('setSubMonthlyNotice', true);
      }
      return res;
    },
    async actionEditManualAdjustment({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'editManualAdjustment', {
        ...params,
        companyId,
      });
      if (!res.error && shouldWarnSubMonthly(params.period)) {
        commit('setSubMonthlyNotice', true);
      }
      return res;
    },
    async actionDeleteManualAdjustment(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'deleteManualAdjustment', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionGetManualAdjustmentComments(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getMAComments', {
        ...params,
        companyId,
      });
      if (!res?.result || !res?.result?.comments) {
        return [];
      }
      return res.result.comments;
    },
    async actionUpdateManualAdjustmentComment(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'updateMAComment', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionDeleteMAComment(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'deleteMAComment', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionGetManualAdjustments({commit}, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getManualAdjustments', {
        ...request,
        companyId,
      });
      commit('setMAList', res.result || null);
      return res;
    },
    async actionGetCurrencyList({commit}) {
      const res = await callAPI('POST', 'getCurrencyList', {});
      commit('setCurrencyList', res?.result?.list);
      return res?.result?.list || [];
    },
    async actionGetCurrencyHistory() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      let pretendedUser = null;
      try {
        pretendedUser = JSON.parse(localStorage.getItem(FYGR_PRETENDED_USER));
      } catch {} // eslint-disable-line
      let token = null;
      if (!pretendedUser) {
        token = localStorage.getItem(FYGR_ACCESS_TOKEN);
      } else {
        token = pretendedUser.token;
      }
      return await fetch(`${urlBase}/company/${companyId}/currenciesHistory`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type':
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });
    },
    async actionGetOngoingTransfers(_commit, accountId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const params = {
        account_id: accountId,
        status: 'pending',
      };
      const queryString = new URLSearchParams(params).toString();
      const res = await callAPI(
        'GET',
        `company/${companyId}/transferOrders?${queryString}`,
      );
      return res?.result;
    },
    async actionDeleteOngoingTransfers(_commit, transferId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      return await callAPI(
        'DELETE',
        `company/${companyId}/transferOrder/${transferId}`,
      );
    },
    async actionArchiveConnection(_commit, connectionId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      return await callAPI('POST', `company/${companyId}/archiveConnection`, {
        connectionId,
      });
    },
    async actionSetMasterCookie() {
      if (
        ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
      ) {
        return;
      }

      function getCookie(name) {
        return (
          document.cookie
            .split('; ')
            .find((c) => c.startsWith(`${name}=`))
            ?.split('=')[1] || null
        );
      }

      const hasCookie = !!getCookie(COOKIE_NAME);
      let id = localStorage.getItem(COOKIE_NAME);

      if (!id) {
        id = window.utils.genId();
        localStorage.setItem(
          COOKIE_NAME,
          hasCookie ? getCookie(COOKIE_NAME) : id,
        );
      }

      if (!hasCookie) {
        try {
          document.cookie = `${COOKIE_NAME}=${id}; path=/; domain=${getCookieDomain()}; SameSite=Lax`;

          await fetch(`${urlBase}/set-mc`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });
        } catch (err) {
          const details = userDetails();
          details.err = err;
          details.errName = 'Master cookie set error';
          sendErrorToBackend(details);
        }
      }
    },
    async actionGetBankTransferUrl(_commit, request) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      return await callAPI('POST', 'transfer', {
        ...request,
        company_id,
      });
    },
    async actionGetExternalTransfer(_commit, externalPaymentId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      return await callAPI(
        'GET',
        `company/${companyId}/transferAttempt/${externalPaymentId}`,
      );
    },
    async actionUpdateAccountBic(_commit, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      return await callAPI(
        'PATCH',
        `company/${company_id}/account/${params.accountId}`,
        {
          bic: params.bic,
        },
      );
    },
    async actionSetAccountLevelForecastingActivated(_commit, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      return await callAPI('PATCH', `setAccountLevelForecastingActivated`, {
        company_id,
        ...params,
      });
    },
    async actionSetDefaultForecastingAccount(_commit, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      return await callAPI('PATCH', `setDefaultForecastingAccount`, {
        company_id,
        ...params,
      });
    },
    async actionSendOTP(_commit, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      return await callAPI('POST', `otp/send`, {
        company_id,
        ...params,
      });
    },
  },
};
